import { shouldSkipShortcut } from '@/lib/keyboard'
import { isModalOpen } from '@/lib/overlayLayers'
import { useShortcut } from '@/lib/useShortcut'
import { useCallback, useEffect } from 'react'
import { KeyboardShortcutsDialog } from './KeyboardShortcutsDialog'
import { useShortcutsDialogStore } from './shortcutsDialogStore'

/** Owns Mod+/ and the shortcuts dialog. Mount it once: a second copy adds a second listener and dialog. */
export function KeyboardShortcutsHost() {
	const isOpen = useShortcutsDialogStore(s => s.isOpen)
	const setOpen = useShortcutsDialogStore(s => s.setOpen)

	const onToggle = useCallback((event: KeyboardEvent) => {
		const store = useShortcutsDialogStore.getState()
		if (shouldSkipShortcut(event, { inTextFields: true })) {
			return
		}
		if (!store.isOpen && isModalOpen()) {
			return
		}
		event.preventDefault()
		store.setOpen(!store.isOpen)
	}, [])
	useShortcut('shortcuts.toggle', onToggle, { skipDefaultGuards: true })

	// `?`: the single-key alternate. Unlike Mod+/, it must not fire while typing — a literal ? is
	// still worth typing in a text field — so this uses the default guards instead of the primary's
	// own `inTextFields: true` check.
	const onToggleAlt = useCallback((event: KeyboardEvent) => {
		const store = useShortcutsDialogStore.getState()
		if (!store.isOpen && isModalOpen()) {
			return
		}
		event.preventDefault()
		store.setOpen(!store.isOpen)
	}, [])
	useShortcut('shortcuts.toggleAlt', onToggleAlt)

	useEffect(() => {
		return () => {
			// The store outlives the layout, so a dialog left open would reappear on the next mount.
			useShortcutsDialogStore.getState().setOpen(false)
		}
	}, [])

	return <KeyboardShortcutsDialog open={isOpen} onOpenChange={setOpen} />
}
